import axios from "axios";

import apiClient from "./axiosConfig";

// SAP's own settings (routes/settings.js). A saved value wins; otherwise the server's env value
// applies, otherwise a default. `null` in a save forgets the saved value, "" saves it as empty.

export type SettingSource = "saved" | "env" | "default" | "unset";

export interface SettingValue<T> {
  value: T | null;
  source: SettingSource;
  /** The env variable used when nothing is saved, if any. */
  env: string | null;
}

export interface PayeeSettings {
  name: SettingValue<string>;
  address: SettingValue<string>;
  gstin: SettingValue<string>;
  supportEmail: SettingValue<string>;
  bankAccountName: SettingValue<string>;
  bankAccountNumber: SettingValue<string>;
  bankIfsc: SettingValue<string>;
  bankName: SettingValue<string>;
  upiId: SettingValue<string>;
  termsUrl: SettingValue<string>;
  privacyUrl: SettingValue<string>;
  refundsUrl: SettingValue<string>;
}

export interface NotificationSettings {
  alertEmails: SettingValue<string[]>;
  emailFromName: SettingValue<string>;
  whatsappReminderTemplate: SettingValue<string>;
  whatsappReceiptTemplate: SettingValue<string>;
}

export interface DashboardSettings {
  slowResponseMs: SettingValue<number>;
  usageWarnPct: SettingValue<number>;
  usageCriticalPct: SettingValue<number>;
}

interface SettingsMeta {
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface BillingSettingsResponse extends SettingsMeta {
  payee: PayeeSettings;
  notifications: NotificationSettings;
}

export interface SystemSettingsResponse extends SettingsMeta {
  dashboard: DashboardSettings;
}

export type IntegrationStatus = "ok" | "attention" | "missing";

export interface Integration {
  key: string;
  label: string;
  status: IntegrationStatus;
  detail: string;
  /** When it last did something (last webhook, last sample, last job run), if known. */
  at: string | null;
}

export type SettingsPatch = Record<string, string | string[] | number | null>;

export const settingsKeys = {
  billing: ["settings", "billing"] as const,
  system: ["settings", "system"] as const,
  integrations: ["settings", "integrations"] as const,
};

export const fetchBillingSettings = async (): Promise<BillingSettingsResponse> =>
  (await apiClient.get<BillingSettingsResponse>("/api/settings/billing")).data;

export const saveBillingSection = async (
  section: "payee" | "notifications",
  patch: SettingsPatch
): Promise<BillingSettingsResponse> =>
  (await apiClient.patch<BillingSettingsResponse>(`/api/settings/billing/${section}`, patch)).data;

export const fetchSystemSettings = async (): Promise<SystemSettingsResponse> =>
  (await apiClient.get<SystemSettingsResponse>("/api/settings/system")).data;

export const saveDashboardSettings = async (
  patch: SettingsPatch
): Promise<SystemSettingsResponse> =>
  (await apiClient.patch<SystemSettingsResponse>("/api/settings/system/dashboard", patch)).data;

export const fetchIntegrations = async (): Promise<Integration[]> =>
  (await apiClient.get<{ integrations: Integration[] }>("/api/settings/integrations")).data
    .integrations;

/** Field messages from a 400 response ({ errors: [{ path, message }] }), keyed by field. */
export const settingsFieldErrors = (err: unknown): Record<string, string> => {
  if (!axios.isAxiosError(err)) {
    return {};
  }
  const errors = (err.response?.data as { errors?: Array<{ path?: string; message?: string }> })
    ?.errors;
  if (!Array.isArray(errors)) {
    return {};
  }
  return errors.reduce<Record<string, string>>((acc, entry) => {
    const field = String(entry.path || "").split(".")[0];
    if (field && entry.message && !acc[field]) {
      acc[field] = entry.message;
    }
    return acc;
  }, {});
};
